import { Modal, Checkbox, Select, Button, Space, Typography } from 'antd';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { postApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import { updateCompanyConnect, updateCompanyStatus } from 'redux/slice/companySlice';
import { transferCompanyAdminAction } from 'redux/action/userAction';
import { CloseSvg } from 'utils/svgs';
import styles from './index.module.scss';

const { Text } = Typography;

interface TransferRoleModalProps {
    isOpen: boolean;
    onCancel: () => void;
    companyId: string;
    allUsers: any[];
    currentUserId: string;
}

const TransferRoleModal = ({
    isOpen,
    onCancel,
    companyId,
    allUsers,
    currentUserId,
}: TransferRoleModalProps) => {
    const [isChecked, setIsChecked] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch<AppDispatch>();
    const { data: companiesData } = useSelector((state: any) => state?.companies);
    const { selectedCompanyDetails } = useSelector((state: any) => state?.companies);

    // Get company name
    const companyName = selectedCompanyDetails?.company?.tenantName || 'this company';

    // Filter active users excluding current user
    const activeUsers = (allUsers || []).filter(
        (user: any) => user.status === true && user.userId !== currentUserId
    );

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setIsChecked(false);
            setSelectedUserId(null);
            setIsLoading(false);
        }
    }, [isOpen]);

    const handleTransfer = async () => {
        if (!isChecked || !selectedUserId) {
            return;
        }

        setIsLoading(true);

        try {
            // Step 1: Check if company is already disconnected
            const company = companiesData?.find(
                (c: any) => c?.companyId === companyId || c?.company?.id === companyId
            );

            if (!company) {
                toastText('Company not found. Please try again.', 'error');
                setIsLoading(false);
                return;
            }

            // Check if company is already disconnected
            const isAlreadyDisconnected = !company?.company?.isConnected;

            // Only disconnect if company is currently connected
            if (!isAlreadyDisconnected) {
                // Get subscription info
                const subscriptions = company?.company?.Subscription;
                const subscription =
                    subscriptions && Array.isArray(subscriptions) ? subscriptions[0] : null;
                const newPlanUid = process.env.REACT_APP_OUTSETA_PLAN_UID_2;

                if (!newPlanUid) {
                    toastText('Something went wrong', 'error');
                    setIsLoading(false);
                    return;
                }

                let isNewOutsetaPlan = false;
                let outSetaAccountUid: string | null = null;

                if (subscription && newPlanUid) {
                    const zohoProductId =
                        subscription?.zohoProductId ||
                        subscription?.zohoSubscriptionPlan?.Plan?.Uid;

                    if (zohoProductId && zohoProductId === newPlanUid) {
                        isNewOutsetaPlan = true;
                    }

                    outSetaAccountUid =
                        subscription?.zohoSubscriptionPlan?.Account?.Uid || null;

                    if (!outSetaAccountUid) {
                        toastText('Unable to find subscription information. Please try again.', 'error');
                        setIsLoading(false);
                        return;
                    }
                } else {
                    toastText('Unable to find subscription information. Please try again.', 'error');
                    setIsLoading(false);
                    return;
                }

                const disconnectPayload: any = {
                    companyId: companyId,
                    outSetaToken: localStorage.getItem('outsetaAccessToken'),
                    isNewOutsetaPlan: isNewOutsetaPlan,
                    outSetaAccountUid: outSetaAccountUid,
                };

                // Disconnect company
                await postApi('/quickbooks/disconnect', disconnectPayload);

                dispatch(
                    updateCompanyConnect({
                        companyId: companyId,
                        value: false,
                    })
                );

                const params = {
                    data: {
                        key: companyId,
                    },
                    value: false,
                };

                dispatch(updateCompanyStatus(params));
            }

            // Step 2: Transfer company admin role
            await dispatch(
                transferCompanyAdminAction({
                    companyId: companyId,
                    targetUserId: selectedUserId,
                })
            ).unwrap();

            // Success - refresh page
            toastText('Company Admin role transferred successfully', 'success');
            window.location.reload();
        } catch (error: any) {
            setIsLoading(false);
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'Something went wrong. Please try again.';
            toastText(errorMessage, 'error');
            // Keep modal open on error
        }
    };

    return (
        <Modal
            open={isOpen}
            onCancel={onCancel}
            footer={null}
            width={600}
            centered
            closeIcon={<CloseSvg />}
            className={styles['transfer-role-modal']}
        >
            <Space direction="vertical" style={{ width: '100%', padding: '10px' }}>
                <Typography.Title level={4} style={{ fontSize: '16px', margin: 0 }}>
                    Transfer Company Admin Role
                </Typography.Title>

                <Text style={{ fontSize: '12px' }}>
                    You are about to transfer the Company Admin role for{' '}
                    <span style={{ fontWeight: 600, color: '#344735' }}>{companyName}</span>.
                    After this change:
                </Text>

                <div style={{ paddingLeft: '20px', fontSize: '12px' }}>
                    <ul style={{ margin: 0 }}>
                        <li>You will become a regular admin user</li>
                        <li>The selected user will become the new Company Admin</li>
                        <li>The new Company Admin must reconnect company to continue using all features</li>
                        <li>Subscription will also be transferred to the new Company Admin&apos;s account when they reconnect the company</li>
                    </ul>
                </div>

                <div
                    style={{
                        backgroundColor: '#fff3cd',
                        marginTop: '10px',
                        padding: '10px',
                        borderRadius: '5px',
                        border: '1px solid #ffeeba',
                    }}
                >
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        Important: Authorization Access Transfer
                    </Text>
                    <Text style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        Only the Company Admin can manage the company connection. Once the role is reassigned:
                    </Text>
                    <ul style={{ margin: '8px 0 0 20px', fontSize: '12px', paddingLeft: '0' }}>
                        <li>This company will be removed from your integration list</li>
                        <li>You will no longer have permission to manage its connection.</li>
                    </ul>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px' }}>
                    <Checkbox
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                    />
                    <Text style={{ marginLeft: '8px', fontSize: '12px' }}>
                        I understand the implications of transferring the Company Admin role
                    </Text>
                </div>

                {isChecked && (
                    <div style={{ marginTop: '15px' }}>
                        <Text style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                            Select User to Transfer Role To{' '}
                            <span className="required-color">*</span>
                        </Text>
                        <Select
                            placeholder="Select a user"
                            style={{ width: '100%' }}
                            size="large"
                            value={selectedUserId}
                            onChange={(value) => setSelectedUserId(value)}
                            showSearch
                            filterOption={(input, option: any) =>
                                (option?.label ?? '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={activeUsers.map((user: any) => ({
                                value: user.userId,
                                label: `${user.name} (${user.email})`,
                            }))}
                        />
                    </div>
                )}

                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'flex-end',
                        marginTop: '20px',
                    }}
                >
                    <Button
                        onClick={onCancel}
                        size="large"
                        disabled={isLoading}
                        style={{
                            backgroundColor: '#fff',
                            color: '#000',
                            borderColor: '#000',
                            fontSize: '12px',
                            borderRadius: '12px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleTransfer}
                        disabled={!isChecked || !selectedUserId || isLoading}
                        loading={isLoading}
                        size="large"
                        style={{
                            backgroundColor: '#344735',
                            borderColor: '#344735',
                            color: !isChecked || !selectedUserId ? '#ececec' : '#fff',
                            fontSize: '12px',
                            borderRadius: '12px',
                        }}
                    >
                        Transfer Your Role
                    </Button>
                </Space>
            </Space>
        </Modal>
    );
};

export default TransferRoleModal;

